import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { AlertCircle, CheckCircle, X, RefreshCw, MessageSquare, Eye, ExternalLink } from 'lucide-react';
import ExportButtons from './ExportButtons';

const ReportedMessagesManager = ({ onNavigateToIPLogs }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedReport, setSelectedReport] = useState(null);
  const [conversationHistory, setConversationHistory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState(null); // 'resolve' or 'dismiss'
  const [actionMessage, setActionMessage] = useState('');
  const [actionReportId, setActionReportId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = statusFilter 
        ? `${API_BASE}/api/admin/messages/reports?status=${statusFilter}`
        : `${API_BASE}/api/admin/messages/reports`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch reports',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch reports',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (reportId, type) => {
    setActionReportId(reportId);
    setActionType(type);
    setActionMessage('');
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setActionReportId(null);
    setActionType(null);
    setActionMessage('');
  };

  const updateReportStatus = async (reportId, newStatus, notes = '') => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/messages/reports/${reportId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          admin_notes: notes
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Report ${newStatus} successfully. Email notification sent to reporter.`
        });
        closeActionModal();
        fetchReports();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to update report',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating report:', error);
      toast({
        title: 'Error',
        description: 'Failed to update report',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAction = () => {
    if (!actionMessage.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a message explaining your decision',
        variant: 'destructive'
      });
      return;
    }

    const status = actionType === 'resolve' ? 'resolved' : 'dismissed';
    updateReportStatus(actionReportId, status, actionMessage);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">Pending</Badge>;
      case 'reviewed':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Reviewed</Badge>;
      case 'resolved':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Resolved</Badge>;
      case 'dismissed':
        return <Badge className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:bg-gray-700 dark:text-gray-200">Dismissed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      'spam': 'Spam',
      'harassment': 'Harassment',
      'inappropriate': 'Inappropriate Content',
      'other': 'Other'
    };
    return labels[reason] || reason;
  };

  const viewConversationHistory = async (report) => {
    try {
      setSelectedReport(report);
      setShowModal(true);
      
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_BASE}/api/admin/messages/reports/${report.id}/conversation`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Conversation History Data:', data);
        console.log('Participants:', data.participants);
        console.log('Participant IDs:', data.participants?.participant_ids);
        console.log('Participant Names:', data.participants?.participant_names);
        setConversationHistory(data);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load conversation history',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error loading conversation:', error);
      toast({
        title: 'Error',
        description: 'Failed to load conversation history',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold dark:text-gray-100">Reported Messages</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Manage user-reported messages
          </p>
        </div>
        <Button onClick={fetchReports} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={reports}
        columns={[
          { header: 'ID', accessor: (row) => row.id || 'N/A' },
          { header: 'Reporter', accessor: (row) => row.reporter_name || 'N/A' },
          { header: 'Reported User', accessor: (row) => row.reported_user_name || 'N/A' },
          { header: 'Reason', accessor: (row) => row.reason || 'N/A' },
          { header: 'Status', accessor: (row) => row.status || 'N/A' },
          { header: 'Created At', accessor: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A' }
        ]}
        filename="reported-messages"
      />

      {/* Filter Tabs */}
      <div className="flex gap-2">
        <Button
          variant={statusFilter === 'pending' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('pending')}
          size="sm"
        >
          Pending
        </Button>
        <Button
          variant={statusFilter === 'reviewed' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('reviewed')}
          size="sm"
        >
          Reviewed
        </Button>
        <Button
          variant={statusFilter === 'resolved' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('resolved')}
          size="sm"
        >
          Resolved
        </Button>
        <Button
          variant={statusFilter === 'dismissed' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('dismissed')}
          size="sm"
        >
          Dismissed
        </Button>
        <Button
          variant={statusFilter === '' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('')}
          size="sm"
        >
          All
        </Button>
      </div>

      {/* Reports Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {reports.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">No reports found</p>
            </CardContent>
          </Card>
        ) : (
          reports.map((report) => (
            <Card key={report.id} className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg dark:text-gray-100 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      {getReasonLabel(report.reason)}
                    </CardTitle>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {new Date(report.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  {getStatusBadge(report.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Reported User:</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{report.reported_user_name}</p>
                </div>
                
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Message Content:</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {report.message_content || 'No content available'}
                  </p>
                </div>

                {report.description && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Description:</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{report.description}</p>
                  </div>
                )}

                {/* View Full Conversation Button - Always Visible */}
                <div className="pt-3">
                  <Button
                    size="sm"
                    variant="default"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    onClick={() => viewConversationHistory(report)}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Full Conversation
                  </Button>
                </div>

                {report.admin_notes && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded">
                    <p className="text-xs font-medium text-blue-900 dark:text-blue-300">Admin Notes:</p>
                    <p className="text-xs text-blue-700 dark:text-blue-400">{report.admin_notes}</p>
                  </div>
                )}

                {/* Actions */}
                {report.status === 'pending' && (
                  <div className="flex gap-2 pt-2 border-t dark:border-gray-700">
                    <Button
                      size="sm"
                      variant="default"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => openActionModal(report.id, 'resolve')}
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Resolve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                      onClick={() => openActionModal(report.id, 'dismiss')}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Dismiss
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Action Modal (Resolve/Dismiss) */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full shadow-xl">
            {/* Modal Header */}
            <div className="p-4 border-b dark:border-gray-700">
              <h3 className="text-lg font-semibold dark:text-gray-100 flex items-center gap-2">
                {actionType === 'resolve' ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Resolve Report
                  </>
                ) : (
                  <>
                    <X className="w-5 h-5 text-red-600" />
                    Dismiss Report
                  </>
                )}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {actionType === 'resolve' 
                  ? 'Explain how you resolved this issue. This message will be sent to the reporter.'
                  : 'Explain why you are dismissing this report. This message will be sent to the reporter.'
                }
              </p>
            </div>

            {/* Modal Body */}
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {actionType === 'resolve' ? 'Resolution Details' : 'Dismissal Reason'}
                <span className="text-red-500">*</span>
              </label>
              <textarea
                value={actionMessage}
                onChange={(e) => setActionMessage(e.target.value)}
                placeholder={actionType === 'resolve' 
                  ? "Example: The reported user has been warned. We've reviewed the conversation and taken appropriate action..."
                  : "Example: After review, we found that this message does not violate our community guidelines..."
                }
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 dark:border dark:border-gray-700-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 text-sm resize-none"
                disabled={submitting}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                This message will be emailed to the person who reported this message.
              </p>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t dark:border-gray-700 flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={closeActionModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant={actionType === 'resolve' ? 'default' : 'destructive'}
                onClick={handleSubmitAction}
                disabled={submitting || !actionMessage.trim()}
                className={actionType === 'resolve' ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    {actionType === 'resolve' ? (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Resolve & Notify
                      </>
                    ) : (
                      <>
                        <X className="w-4 h-4 mr-2" />
                        Dismiss & Notify
                      </>
                    )}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Conversation History Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold dark:text-gray-100 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Full Conversation History
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {conversationHistory ? 
                    `${conversationHistory.total_messages} messages total` : 
                    'Loading...'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowModal(false);
                  setConversationHistory(null);
                }}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body - Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {!conversationHistory ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : (
                conversationHistory.messages.map((message, index) => (
                  <div
                    key={message.id}
                    className={`p-3 rounded-lg ${
                      message.is_reported_message
                        ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700'
                        : 'bg-gray-50 dark:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm font-semibold dark:text-gray-200">
                          {message.sender_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(message.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      {message.is_reported_message && (
                        <Badge className="bg-red-600 text-white">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          Reported Message
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {message.content}
                    </p>
                    {message.is_edited && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">
                        (edited)
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <strong className="text-gray-700 dark:text-gray-300">Participants:</strong>
                  {conversationHistory?.participants ? (
                    <div className="flex items-center gap-2">
                      {conversationHistory.participants.participant_names?.map((name, index) => {
                        const userId = conversationHistory.participants.participant_ids?.[index];
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              console.log('Participant clicked:', name, userId);
                              setShowModal(false);
                              setConversationHistory(null);
                              if (onNavigateToIPLogs && userId) {
                                onNavigateToIPLogs(userId);
                              }
                            }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-medium rounded-lg shadow-sm transition-all hover:shadow-md transform hover:scale-105 cursor-pointer"
                          >
                            {name}
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400">Loading...</span>
                  )}
                </div>
                <Button
                  onClick={() => {
                    setShowModal(false);
                    setConversationHistory(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportedMessagesManager;